/**
 * MobileJobCard Component
 * Mobile-first job card for delivery users
 * Optimized for phone screens with easy-to-read layout
 *
 * Delivered / incomplete: same “wash” as delivery calendar JobEventCard DEL —
 * saturated fill + whole-card opacity 0.6 + white text (not pastel + backdrop-blur).
 */

import React from 'react';
import { Phone, MapPin, Package, Clock, ChevronRight, Check, AlertTriangle } from 'lucide-react';

/** Match JobEventCard getStatusBorderAndOpacity DEL treatment */
const CALENDAR_WASH_OPACITY = 0.6;
const DELIVERED_BG = '#22c55e';
const DELIVERED_BORDER = '#22c55e';
const INCOMPLETE_BG = '#f97316';
const INCOMPLETE_BORDER = '#ea580c';

interface MobileJobCardProps {
  job: {
    id: string;
    website_job_number: string;
    reference: string | null;
    delivery_postcode: string;
    customer_name?: string | null;
    customer_first_name?: string | null;
    customer_last_name?: string | null;
    customer_mobile_number?: string | null;
    delivery_address_line1: string;
    delivery_address_line2?: string | null;
    total_items: number;
    potential_delivery_date: string;
    delivery_window_start?: number | null;
    delivery_window_end?: number | null;
    delivery_status?: string | null;
    delivery_notes?: string | null;
    delivery_result?: 'pending' | 'delivered' | 'incomplete' | null;
  };
  onView: (jobId: string) => void;
}

export const MobileJobCard: React.FC<MobileJobCardProps> = ({ job, onView }) => {
  const customerName = job.customer_name ||
    `${job.customer_first_name || ''} ${job.customer_last_name || ''}`.trim() ||
    'Unknown Customer';

  const fullAddress = [job.delivery_address_line1, job.delivery_address_line2]
    .filter(Boolean)
    .join(', ');

  const formatTimeWindow = () => {
    if (job.delivery_window_start && job.delivery_window_end) {
      const start = String(job.delivery_window_start).padStart(2, '0');
      const end = String(job.delivery_window_end).padStart(2, '0');
      return `${start}:00 - ${end}:00`;
    }
    if (job.delivery_window_start) {
      const start = String(job.delivery_window_start).padStart(2, '0');
      return `${start}:00 onwards`;
    }
    if (job.delivery_window_end) {
      const end = String(job.delivery_window_end).padStart(2, '0');
      return `Until ${end}:00`;
    }
    return 'Time TBC';
  };

  const isWash =
    job.delivery_result === 'delivered' || job.delivery_result === 'incomplete';

  const getLeftBorderClass = () => {
    switch (job.delivery_status?.toLowerCase()) {
      case 'confirmed':
        return 'border-l-green-500';
      case 'voicemail':
        return 'border-l-yellow-500';
      case 'voicemail_email':
        return 'border-l-orange-500';
      case 'redelivery':
        return 'border-l-red-500';
      default:
        return 'border-l-gray-400';
    }
  };

  const pendingSurfaceClass = 'bg-white border-gray-200 shadow-sm';

  const washRootStyle: React.CSSProperties | undefined = isWash
    ? {
        backgroundColor:
          job.delivery_result === 'delivered' ? DELIVERED_BG : INCOMPLETE_BG,
        color: '#ffffff',
        opacity: CALENDAR_WASH_OPACITY,
        borderColor:
          job.delivery_result === 'delivered' ? DELIVERED_BORDER : INCOMPLETE_BORDER,
        borderWidth: 1,
        borderStyle: 'solid',
        borderLeftWidth: 4,
      }
    : undefined;

  const bd = isWash ? 'border-b border-white/20' : 'border-b border-gray-100';
  const label = isWash ? 'text-white/75' : 'text-gray-500';
  const text = isWash ? 'text-white' : 'text-gray-900';
  const orderLabel = isWash ? 'text-white/70' : 'text-gray-500';
  const orderNum = isWash ? 'text-white' : 'text-gray-800';
  const iconCircle = (tint: 'blue' | 'purple' | 'green') =>
    isWash
      ? 'bg-white/20'
      : tint === 'blue'
        ? 'bg-blue-100'
        : tint === 'purple'
          ? 'bg-purple-100'
          : 'bg-green-100';
  const iconColor = (tint: 'blue' | 'purple' | 'green') =>
    isWash ? 'text-white' : tint === 'blue' ? 'text-blue-600' : tint === 'purple' ? 'text-purple-600' : 'text-green-600';

  return (
    <div
      onClick={() => onView(job.id)}
      style={washRootStyle}
      className={`
        rounded-xl border overflow-hidden border-l-4
        ${isWash ? '' : `${pendingSurfaceClass} ${getLeftBorderClass()}`}
        active:scale-[0.98] transition-all duration-150
        cursor-pointer
      `}
    >
      <div className={`p-4 ${bd}`}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex-1">
            <span className={`text-2xl font-bold tracking-tight ${text}`}>
              {job.delivery_postcode}
            </span>
          </div>

          {job.delivery_result === 'delivered' && (
            <span
              className={`px-2 py-1 text-xs font-bold rounded-full flex items-center gap-1 ${
                isWash ? 'bg-black/40 text-white' : 'bg-green-100 text-green-700'
              }`}
            >
              <Check className="w-3 h-3" />
              Delivered
            </span>
          )}
          {job.delivery_result === 'incomplete' && (
            <span
              className={`px-2 py-1 text-xs font-bold rounded-full flex items-center gap-1 ${
                isWash ? 'bg-black/40 text-white' : 'bg-orange-100 text-orange-700'
              }`}
            >
              <AlertTriangle className="w-3 h-3" />
              Incomplete
            </span>
          )}

          <div className="text-right">
            <span className={`text-xs ${orderLabel}`}>Order</span>
            <p className={`text-base font-bold ${orderNum}`}>
              #{job.website_job_number || '-'}
            </p>
          </div>
        </div>

        <div className="mt-2">
          <h3 className={`text-lg font-semibold truncate ${text}`}>{customerName}</h3>
        </div>
      </div>

      <div className={`p-4 grid grid-cols-2 gap-4 ${bd}`}>
        <div className="flex items-center gap-2">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${iconCircle('blue')}`}
          >
            <Clock className={`w-4 h-4 ${iconColor('blue')}`} />
          </div>
          <div>
            <p className={`text-xs ${label}`}>Time</p>
            <p className={`text-sm font-medium ${text}`}>{formatTimeWindow()}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${iconCircle('purple')}`}
          >
            <Package className={`w-4 h-4 ${iconColor('purple')}`} />
          </div>
          <div>
            <p className={`text-xs ${label}`}>Items</p>
            <p className={`text-sm font-medium ${text}`}>{job.total_items || 0}</p>
          </div>
        </div>
      </div>

      <div className={`p-4 ${bd}`}>
        <div className="flex items-start gap-3">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${iconCircle('green')}`}
          >
            <MapPin className={`w-4 h-4 ${iconColor('green')}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-xs mb-0.5 ${label}`}>Delivery Address</p>
            <p className={`text-sm leading-snug break-words ${text}`}>
              {fullAddress || job.delivery_address_line1}
            </p>
            <p className={`text-sm font-medium mt-0.5 ${text}`}>{job.delivery_postcode}</p>
          </div>
        </div>
      </div>

      <div className="p-4 flex items-center justify-between gap-3">
        {job.customer_mobile_number ? (
          <a
            href={`tel:${job.customer_mobile_number}`}
            onClick={(e) => e.stopPropagation()}
            className={
              isWash
                ? 'flex items-center gap-2 flex-1 bg-white/20 hover:bg-white/30 active:bg-white/40 rounded-lg px-3 py-2.5 transition-colors'
                : 'flex items-center gap-2 flex-1 bg-green-50 hover:bg-green-100 active:bg-green-200 rounded-lg px-3 py-2.5 transition-colors'
            }
          >
            <Phone className={`w-5 h-5 ${isWash ? 'text-white' : 'text-green-600'}`} />
            <span
              className={`text-sm font-semibold ${isWash ? 'text-white' : 'text-green-700'}`}
            >
              {job.customer_mobile_number}
            </span>
          </a>
        ) : (
          <div
            className={`flex items-center gap-2 flex-1 rounded-lg px-3 py-2.5 ${
              isWash ? 'bg-white/10' : 'bg-gray-50'
            }`}
          >
            <Phone className={`w-5 h-5 ${isWash ? 'text-white/50' : 'text-gray-400'}`} />
            <span className={`text-sm ${isWash ? 'text-white/60' : 'text-gray-400'}`}>
              No phone
            </span>
          </div>
        )}

        <button
          type="button"
          className="flex items-center gap-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white rounded-lg font-medium text-sm transition-colors"
        >
          View
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {job.delivery_notes && (
        <div className="px-4 pb-4">
          <div
            className={
              isWash
                ? 'bg-black/25 border border-white/25 rounded-lg p-3'
                : 'bg-yellow-50 border border-yellow-200 rounded-lg p-3'
            }
          >
            <p
              className={`text-xs font-medium mb-1 ${
                isWash ? 'text-white/90' : 'text-yellow-700'
              }`}
            >
              Delivery Note:
            </p>
            <p className={`text-sm line-clamp-2 ${isWash ? 'text-white' : 'text-yellow-900'}`}>
              {job.delivery_notes}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

MobileJobCard.displayName = 'MobileJobCard';
